import EventEmitter from 'eventemitter3'

/**
 * A unified streaming response that supports three different consumption patterns:
 * 1. Async iteration: `for await (const chunk of response) { ... }`
 * 2. Event-based: `response.on('data', chunk => { ... })`
 * 3. Promise-based: `const result = await response` (resolves with final accumulated result)
 */
export class StreamableResponse<T = string> extends EventEmitter implements AsyncIterable<T> {
  private readonly sourceIterable: AsyncIterable<T>
  private _isConsumed = false
  private _accumulatedResult: T | null = null
  private _promiseResolver: ((value: T) => void) | null = null
  private _promiseRejecter: ((error: Error) => void) | null = null
  private _consumePromise: Promise<T> | null = null

  constructor(sourceIterable: AsyncIterable<T>) {
    super()
    this.sourceIterable = sourceIterable
  }

  /**
   * Async iterator implementation - supports `for await...of` loops
   */
  async *[Symbol.asyncIterator](): AsyncIterator<T> {
    if (this._isConsumed) {
      throw new Error('StreamableResponse can only be consumed once. Use .clone() to consume multiple times.')
    }
    
    this._isConsumed = true
    
    try {
      for await (const chunk of this.sourceIterable) {
        this._accumulatedResult = chunk // Keep the latest (accumulated) value
        this.emit('data', chunk)
        yield chunk
      }
      
      this.emit('end')
      if (this._promiseResolver && this._accumulatedResult !== null) {
        this._promiseResolver(this._accumulatedResult)
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      this.emit('error', err)
      if (this._promiseRejecter) {
        this._promiseRejecter(err)
      }
      throw err
    }
  }

  /**
   * Promise implementation - enables `await response` to get final result
   */
  then<TResult1 = T, TResult2 = never>(
    onFulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | null,
    onRejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    if (!this._consumePromise) {
      this._consumePromise = new Promise<T>((resolve, reject) => {
        this._promiseResolver = resolve
        this._promiseRejecter = reject
        
        // Start consuming the stream if not already started
        if (!this._isConsumed) {
          this._consumeInBackground()
        }
      })
    }
    
    return this._consumePromise.then(onFulfilled, onRejected)
  }

  /**
   * Promise catch implementation
   */
  catch<TResult = never>(
    onRejected?: ((reason: any) => TResult | PromiseLike<TResult>) | null
  ): Promise<T | TResult> {
    return this.then(undefined, onRejected)
  }

  /**
   * Promise finally implementation
   */
  finally(onFinally?: (() => void) | null): Promise<T> {
    return this.then(
      (value) => {
        onFinally?.()
        return value
      },
      (reason) => {
        onFinally?.()
        throw reason
      }
    )
  }

  /**
   * Enhanced event listener with proper typing
   */
  on(event: 'data', listener: (chunk: T) => void): this
  on(event: 'end', listener: () => void): this
  on(event: 'error', listener: (error: Error) => void): this
  on(event: string, listener: (...args: any[]) => void): this {
    // Auto-start consumption when a data listener is added
    if (event === 'data' && !this._isConsumed && !this._consumePromise) {
      this._consumeInBackground()
    }
    
    return super.on(event, listener)
  }

  /**
   * Enhanced once listener with proper typing
   */
  once(event: 'data', listener: (chunk: T) => void): this
  once(event: 'end', listener: () => void): this  
  once(event: 'error', listener: (error: Error) => void): this
  once(event: string, listener: (...args: any[]) => void): this {
    // Auto-start consumption when a data listener is added
    if (event === 'data' && !this._isConsumed && !this._consumePromise) {
      this._consumeInBackground()
    }
    
    return super.once(event, listener)
  }

  /**
   * Start consuming the stream in the background for event listeners and promise resolution
   */
  private async _consumeInBackground(): Promise<void> {
    try {
      for await (const chunk of this) {
        // The async iterator handles all the event emission and promise resolution
      }
    } catch (error) {
      // Error handling is done in the async iterator
    }
  }

  /**
   * Clone the streamable response to allow multiple consumption patterns
   * Note: This requires the underlying source to be re-iterable or cloneable
   */
  clone(): StreamableResponse<T> {
    if (this._isConsumed) {
      throw new Error('Cannot clone a StreamableResponse that has already been consumed')
    }
    return new StreamableResponse(this.sourceIterable)
  }

  /**
   * Check if the stream has been consumed
   */
  get isConsumed(): boolean {
    return this._isConsumed
  }
}